import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Tooltip } from '@Sage/hoi-poi-ui';

import './styles.scss';

const TruncatedTooltip = memo(function TruncatedTooltip({ children, className, ...props }) {
    const [isEllipsisActive, setEllipsisActive] = useState(false);
    const isEllipsisActiveRef = useRef(false);
    const textEl = useRef(null);
    const classes = ['fm-truncate-tooltip'];
    if (className) classes.push(className);

    const measureEllipsis = useCallback(() => {
        const el = textEl.current;
        if (!el) return;

        const nextIsEllipsisActive = el.offsetWidth < el.scrollWidth;
        if (nextIsEllipsisActive === isEllipsisActiveRef.current) return;

        isEllipsisActiveRef.current = nextIsEllipsisActive;
        setEllipsisActive(nextIsEllipsisActive);
    }, []);

    useLayoutEffect(() => {
        const el = textEl.current;
        if (!el) return undefined;

        measureEllipsis();

        const resizeObserver =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureEllipsis);
        const mutationObserver = new MutationObserver(measureEllipsis);
        resizeObserver?.observe(el);
        mutationObserver.observe(el, { childList: true, characterData: true, subtree: true });
        window.addEventListener('resize', measureEllipsis);

        return () => {
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', measureEllipsis);
        };
    }, [measureEllipsis]);

    const trigger = (
        <span className="fm-truncate-tooltip__trigger">
            <span className="fm-truncate-tooltip__text" ref={textEl}>
                {children}
            </span>
        </span>
    );

    return (
        <span className={classes.join(' ')}>
            <Tooltip {...props} content={isEllipsisActive ? props.content : undefined}>
                {trigger}
            </Tooltip>
        </span>
    );
});

TruncatedTooltip.displayName = 'TruncatedTooltip';

TruncatedTooltip.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

export default TruncatedTooltip;
